import { Controller } from 'stimulus';
import { teamDateFormat } from '@/js/modules/datepicker';

const DAY_MS = 86_400_000;
const GAP_PX = 4;
const CENTURY_PIVOT = 50;
const ARROW_STEPS = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };

const toIso = (date) => date.toISOString().slice(0, 10);
const fromIso = (iso) => new Date(`${iso}T00:00:00Z`);
const today = () => {
  const now = new Date();
  return toIso(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
};
const shiftDays = (iso, days) => toIso(new Date(fromIso(iso).getTime() + days * DAY_MS));
const firstWeekday = (locale) => {
  try {
    const tag = new Intl.Locale(locale);
    return ((tag.getWeekInfo?.() ?? tag.weekInfo)?.firstDay ?? 7) % 7;
  } catch {
    return 0;
  }
};
const shiftMonths = (iso, months) => {
  const date = fromIso(iso);
  const first = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
  const lastDay = new Date(
    Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)
  ).getUTCDate();
  first.setUTCDate(Math.min(date.getUTCDate(), lastDay));
  return toIso(first);
};

export default class extends Controller {
  static targets = [
    'value',
    'display',
    'calendar',
    'heading',
    'weekdays',
    'days',
    'hint',
    'clear',
    'picker',
  ];
  static values = { format: String, min: String, max: String, locale: String };

  connect() {
    const locale = this.localeValue || navigator.language;
    this.firstWeekday = firstWeekday(locale);
    this.monthFormatter = new Intl.DateTimeFormat(locale, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });
    this.weekdayFormatter = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
    this.weekdayNameFormatter = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      timeZone: 'UTC',
    });
    this.dateFormatter = new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeZone: 'UTC' });
    this.#renderWeekdays();
    this.displayTarget.placeholder = this.#pattern;
    this.hintTarget.textContent = `Date as ${this.#pattern}`;
    this.#syncDisplay();
    this.valueTarget.form?.addEventListener('reset', this.#reset);
    this.ready = true;
  }

  disconnect() {
    this.valueTarget.form?.removeEventListener('reset', this.#reset);
    this.#unwatch();
  }

  minValueChanged() {
    this.#boundsChanged();
  }

  maxValueChanged() {
    this.#boundsChanged();
  }

  open() {
    if (!this.#open && !this.#platformPicker) {
      this.calendarTarget.showPopover();
    }
  }

  focused(event) {
    if (!this.element.contains(event.relatedTarget)) {
      this.open();
    }
  }

  toggle(event) {
    if (this.#platformPicker) {
      event.preventDefault();
      this.#showPlatformPicker();
    }
  }

  picked() {
    if (this.pickerTarget.value) {
      this.#select(this.pickerTarget.value);
    }
  }

  clear() {
    this.#select('');
    this.displayTarget.focus();
    this.#close();
  }

  openAndFocus(event) {
    event.preventDefault();
    if (this.#platformPicker) {
      this.#showPlatformPicker();
      return;
    }

    this.open();
    this.#focusDay(this.valueTarget.value || today());
  }

  toggling(event) {
    this.opened = event.newState === 'open';
    if (!this.opened && this.calendarTarget.contains(document.activeElement)) {
      this.displayTarget.focus();
    }
  }

  toggled(event) {
    if (event.newState !== 'open') {
      this.#unwatch();
      return;
    }

    this.#showMonth((this.valueTarget.value || today()).slice(0, 7));
    this.#place();
    window.addEventListener('resize', this.#place);
    document.addEventListener('scroll', this.#place, true);
    if (document.activeElement !== this.displayTarget) {
      this.#focusDay(this.valueTarget.value || today());
    }
  }

  focusLeft(event) {
    if (event.relatedTarget && !this.element.contains(event.relatedTarget)) {
      this.#close();
    }
  }

  previousMonth() {
    this.#showMonth(shiftMonths(`${this.month}-01`, -1).slice(0, 7));
  }

  nextMonth() {
    this.#showMonth(shiftMonths(`${this.month}-01`, 1).slice(0, 7));
  }

  pick(event) {
    const button = event.target.closest('button[value]');
    if (!button || button.disabled) {
      return;
    }

    this.#select(button.value);
    this.#close();
  }

  navigate(event) {
    const current = event.target.closest('button[value]');
    if (!current) {
      return;
    }

    const iso = current.value;
    let next;
    if (event.key in ARROW_STEPS) {
      next = shiftDays(iso, ARROW_STEPS[event.key]);
    } else if (event.key === 'Home') {
      next = shiftDays(iso, -this.#weekOffset(iso));
    } else if (event.key === 'End') {
      next = shiftDays(iso, 6 - this.#weekOffset(iso));
    } else if (event.key === 'PageUp') {
      next = shiftMonths(iso, -1);
    } else if (event.key === 'PageDown') {
      next = shiftMonths(iso, 1);
    } else {
      return;
    }

    event.preventDefault();
    this.#showMonth(next.slice(0, 7));
    this.#focusDay(next);
  }

  parse() {
    const iso = this.#validate();
    if (iso === null) {
      this.#close();
      this.#store('');
    } else {
      this.#select(iso);
    }
  }

  typed() {
    this.#validate();
    this.#syncClear();
  }

  commit(event) {
    event.preventDefault();
    this.parse();
    this.#close();
    if (!this.displayTarget.validity.valid) {
      this.displayTarget.reportValidity();
    }
  }

  #reset = () => {
    setTimeout(() => this.#syncDisplay());
  };

  #validate() {
    const text = this.displayTarget.value.trim();
    const iso = text ? this.#parse(text) : '';
    const problem = text ? this.#problem(iso) : '';
    this.displayTarget.setCustomValidity(problem);
    return problem ? null : iso;
  }

  #boundsChanged() {
    if (!this.ready) {
      return;
    }

    this.#store(this.#validate() ?? '');
    this.#rerenderIfOpen();
  }

  #select(iso) {
    this.#store(iso);
    this.#syncDisplay();
  }

  #store(iso) {
    if (iso === this.valueTarget.value) {
      return;
    }

    this.valueTarget.value = iso;
    this.valueTarget.dispatchEvent(new Event('input', { bubbles: true }));
    this.valueTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #syncDisplay() {
    this.displayTarget.value = this.#format(this.valueTarget.value);
    this.displayTarget.setCustomValidity('');
    this.#syncClear();
  }

  #syncClear() {
    if (this.hasClearTarget) {
      this.clearTarget.hidden = !this.displayTarget.value;
    }
  }

  #unwatch() {
    window.removeEventListener('resize', this.#place);
    document.removeEventListener('scroll', this.#place, true);
  }

  #rerenderIfOpen() {
    if (this.#open) {
      this.#render();
    }
  }

  get #open() {
    return this.opened === true;
  }

  get #platformPicker() {
    return (
      this.hasPickerTarget &&
      typeof this.pickerTarget.showPicker === 'function' &&
      window.matchMedia?.('(pointer: coarse)').matches === true
    );
  }

  #showPlatformPicker() {
    this.pickerTarget.value = this.valueTarget.value;
    this.pickerTarget.min = this.minValue;
    this.pickerTarget.max = this.maxValue;
    try {
      this.pickerTarget.showPicker();
    } catch {
      this.calendarTarget.showPopover();
    }
  }

  #close() {
    if (this.#open) {
      this.calendarTarget.hidePopover();
    }
  }

  #inRange(iso) {
    return (!this.minValue || iso >= this.minValue) && (!this.maxValue || iso <= this.maxValue);
  }

  #problem(iso) {
    if (!iso) {
      return `Enter a date as ${this.#pattern}`;
    }
    if (this.minValue && iso < this.minValue) {
      return `Choose a date on or after ${this.#format(this.minValue)}`;
    }
    if (this.maxValue && iso > this.maxValue) {
      return `Choose a date on or before ${this.#format(this.maxValue)}`;
    }
    return '';
  }

  #weekOffset(iso) {
    return (fromIso(iso).getUTCDay() - this.firstWeekday + 7) % 7;
  }

  #renderWeekdays() {
    const sunday = Date.UTC(2023, 0, 1);
    const cells = Array.from({ length: 7 }, (_, column) => {
      const day = sunday + ((this.firstWeekday + column) % 7) * DAY_MS;
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.abbr = this.weekdayNameFormatter.format(day);
      cell.textContent = this.weekdayFormatter.format(day);
      return cell;
    });
    this.weekdaysTarget.replaceChildren(...cells);
  }

  #showMonth(month) {
    this.month = month;
    this.#render();
  }

  #render() {
    const [year, month] = this.month.split('-').map(Number);
    const first = Date.UTC(year, month - 1, 1);
    const last = Date.UTC(year, month, 0);
    this.headingTarget.textContent = this.monthFormatter.format(first);

    const rows = [];
    for (
      let cursor = first - this.#weekOffset(toIso(new Date(first))) * DAY_MS;
      cursor <= last;
      cursor += 7 * DAY_MS
    ) {
      const row = document.createElement('tr');
      for (let day = 0; day < 7; day++) {
        const date = new Date(cursor + day * DAY_MS);
        const cell = document.createElement('td');
        cell.setAttribute('role', 'gridcell');
        if (date.getUTCMonth() === month - 1) {
          cell.append(this.#dayButton(date));
          cell.setAttribute('aria-selected', toIso(date) === this.valueTarget.value);
        }
        row.append(cell);
      }
      rows.push(row);
    }
    this.daysTarget.replaceChildren(...rows);
  }

  #dayButton(date) {
    const iso = toIso(date);
    const button = document.createElement('button');
    button.type = 'button';
    button.value = iso;
    button.tabIndex = -1;
    button.disabled = !this.#inRange(iso);
    button.textContent = date.getUTCDate();
    button.setAttribute('aria-label', this.dateFormatter.format(date));
    if (iso === today()) {
      button.setAttribute('aria-current', 'date');
    }
    return button;
  }

  #focusDay(iso) {
    const buttons = [...this.daysTarget.querySelectorAll('button:enabled')];
    const target = buttons.find((button) => button.value === iso) ?? buttons[0];
    this.daysTarget.querySelectorAll('button').forEach((button) => (button.tabIndex = -1));
    if (target) {
      target.tabIndex = 0;
      target.focus();
    }
  }

  #place = () => {
    const { left, right, top, bottom } = this.displayTarget.getBoundingClientRect();
    const { offsetWidth: width, offsetHeight: height } = this.calendarTarget;
    const fitsBelow = bottom + GAP_PX + height <= window.innerHeight || top - GAP_PX - height < 0;
    const fitsRight = left + width <= window.innerWidth;

    this.calendarTarget.style.left = `${fitsRight ? left : Math.max(0, right - width)}px`;
    this.calendarTarget.style.top = `${fitsBelow ? bottom + GAP_PX : top - GAP_PX - height}px`;
  };

  get #pattern() {
    return this.hasFormatValue ? this.formatValue : teamDateFormat();
  }

  #format(iso) {
    if (!iso) {
      return '';
    }

    const [year, month, day] = iso.split('-');
    return this.#pattern.replace('yyyy', year).replace('mm', month).replace('dd', day);
  }

  #parse(text) {
    const source = this.#pattern
      .replace(/[.\/-]/g, '\\$&')
      .replace('yyyy', '(?<year>\\d{2}|\\d{4})')
      .replace('mm', '(?<month>\\d{1,2})')
      .replace('dd', '(?<day>\\d{1,2})');
    const match = text.trim().match(new RegExp(`^${source}$`));
    if (!match) {
      return null;
    }

    const { month, day } = match.groups;
    let year = Number(match.groups.year);
    if (match.groups.year.length === 2) {
      year += year < CENTURY_PIVOT ? 2000 : 1900;
    }
    const date = new Date(Date.UTC(year, Number(month) - 1, Number(day)));
    return date.getUTCMonth() === Number(month) - 1 ? toIso(date) : null;
  }
}
